import { apiClient, postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { deleteFile, toUploadedFile, uploadImage, type RawFile, type UploadedFile } from '@/shared/api/upload';
import { inputValueToUnix, unixToInputValue } from '@/shared/lib/datetime';
import { getListRows } from '@/shared/lib/pagination';
import { normalizeOptions } from '@/shared/lib/normalizeOptions';
import type { ListResponse } from '@/shared/ui/list/types';

const ALL_ROWS_LIMIT = 10_000;

export async function fetchCategoryOptions() {
  const res = await postForm<ListResponse>('/publication/category/index/', {
    page: 1,
    limit: ALL_ROWS_LIMIT,
  });
  return normalizeOptions(getListRows(res, ['items']));
}

/** Stories list is a GET (the client adds `auth_token` to the query). */
export const fetchStoriesList = () =>
  apiClient.get<ListResponse>('/stories/preview/list/').then((res) => res.data);

export const deleteArticle = (id: number) => submitForm('/publication/article/delete/', { id });

export const saveCategory = (
  id: number | undefined,
  v: { title: string; body: string; slug: string; code: string; statusId: string },
) =>
  submitForm(id ? '/publication/category/update/' : '/publication/category/create/', {
    id,
    title: v.title,
    body: v.body,
    slug: v.slug,
    code: v.code,
    status_id: v.statusId,
  });

export const deleteCategory = (id: number) => submitForm('/publication/category/delete/', { id });

export const saveTag = (
  id: number | undefined,
  v: { name: string; slug: string; sortId: string },
) =>
  submitForm(id ? '/publication/tag/update/' : '/publication/tag/create/', {
    id,
    name: v.name,
    slug: v.slug,
    sort_id: id ? undefined : v.sortId,
  });

export const deleteTag = (id: number) => submitForm('/publication/tag/delete/', { id });

export const deletePress = (id: number) => submitForm('/publication/mass-media/delete/', { id });

export const deleteStories = (id: number) => submitForm('/stories/preview/delete/', { id });

// ---- stories detail (slides of a story preview) ----------------------------

export const SLIDE_TYPE_ACTION = 'action';

export interface SlideValues {
  name: string;
  type: string;
  gradientFirst: string;
  gradientSecond: string;
  orientation: string;
  image: UploadedFile[];
  typeButton: string;
  textButton: string;
  uriButton: string;
  styleButton: string;
}

export const EMPTY_SLIDE: SlideValues = {
  name: '',
  type: '',
  gradientFirst: '',
  gradientSecond: '',
  orientation: '',
  image: [],
  typeButton: '',
  textButton: '',
  uriButton: '',
  styleButton: '',
};

export interface Slide extends Record<string, unknown> {
  id: number;
  name?: string;
  type?: string;
  content_image?: RawFile | null;
  gradient_colors_1?: string;
  gradient_colors_2?: string;
  orientation?: string;
  type_button?: string;
  uri_button?: string;
  text_button?: string;
  style_button?: string;
  create_time?: number;
  update_time?: number;
}

export const fetchSlides = (previewId: number) =>
  apiClient
    .get<ListResponse>('/stories/content/list/', { params: { preview_id: previewId } })
    .then((res) => res.data);

/** Image already uploaded for a new slide but not attached to any (legacy preloads it into the create form). */
export async function fetchUnattachedSlideImage() {
  const res = await apiClient.get<{ data?: RawFile[] }>('/stories/content/get-unattached-image/');
  return toUploadedFile(res.data.data?.[0]);
}

export const slideToValues = (slide: Slide): SlideValues => {
  const image = toUploadedFile(slide.content_image);
  return {
    name: slide.name ?? '',
    type: slide.type ?? '',
    gradientFirst: slide.gradient_colors_1 ?? '',
    gradientSecond: slide.gradient_colors_2 ?? '',
    orientation: slide.orientation ?? '',
    image: image ? [image] : [],
    typeButton: slide.type_button ?? '',
    textButton: slide.text_button ?? '',
    uriButton: slide.uri_button ?? '',
    styleButton: slide.style_button ?? '',
  };
};

export const uploadSlideImage = (file: File) => uploadImage('/stories/content/upload-image/', file);
export const deleteSlideImage = (id: number) => deleteFile('/stories/content/delete-image/', id);

export const saveSlide = (previewId: number, id: number | undefined, v: SlideValues) => {
  const isAction = v.type === SLIDE_TYPE_ACTION;
  return submitForm(`/stories/content/${id ? 'update' : 'create'}/`, {
    id,
    preview_id: previewId,
    name: v.name,
    type: v.type,
    gradient_colors_1: v.gradientFirst,
    gradient_colors_2: v.gradientSecond,
    orientation: v.orientation,
    image_id: v.image[0]?.id,
    type_button: isAction ? v.typeButton : '',
    text_button: isAction ? v.textButton : '',
    uri_button: isAction ? v.uriButton : '',
    style_button: isAction ? v.styleButton : '',
  });
};

export const deleteSlide = (id: number) => submitForm('/stories/content/delete/', { id });

// ---- article create / edit -------------------------------------------------

export async function fetchTagOptions() {
  const res = await postForm<ListResponse>('/publication/tag/index/', { page: 1, limit: ALL_ROWS_LIMIT });
  return normalizeOptions(getListRows(res, ['items']));
}

export const ARTICLE_STATUS_OPTIONS = [
  { value: '0', label: 'Не опубликовано' },
  { value: '1', label: 'Опубликовано' },
  { value: '2', label: 'Опубликовано первым' },
];

/** Categories ("Новости", "Статьи") whose articles can be exported to RSS. */
const RSS_CATEGORY_IDS = ['6', '9'];
export const isRssCategory = (categoryId: string) => RSS_CATEGORY_IDS.includes(categoryId);

export interface ArticleValues {
  title: string;
  statusId: string;
  slug: string;
  categoryId: string;
  tagIds: string[];
  publishedTime: string;
  ogTitle: string;
  ogDescription: string;
  rssView: boolean;
  redirect: string;
  body: string;
}

export const emptyArticle = (): ArticleValues => ({
  title: '',
  statusId: '',
  slug: '',
  categoryId: '',
  tagIds: [],
  publishedTime: unixToInputValue(Math.floor(Date.now() / 1000)),
  ogTitle: '',
  ogDescription: '',
  rssView: false,
  redirect: '',
  body: '',
});

export interface ArticleItem extends Record<string, unknown> {
  id: number;
  title?: string;
  slug?: string;
  body?: string;
  status_id?: number | string;
  category?: { id: number } | null;
  tags?: { id: number }[] | null;
  published_time?: number;
  og_title?: string;
  og_description?: string;
  rss_view?: number | boolean;
  redirect?: string | null;
  /** Title picture; `[]` / null when absent. */
  preview?: RawFile | RawFile[] | null;
  /** Pictures for the article body. */
  images?: RawFile[] | null;
}

export async function fetchArticle(id: number) {
  const res = await postForm<{ article?: ArticleItem }>('/publication/article/view/', { id });
  return res.article;
}

export const articleToValues = (item: ArticleItem): ArticleValues => ({
  title: item.title ?? '',
  statusId: String(item.status_id ?? ''),
  slug: item.slug ?? '',
  categoryId: String(item.category?.id ?? ''),
  tagIds: (item.tags ?? []).map((tag) => String(tag.id)),
  publishedTime: unixToInputValue(item.published_time),
  ogTitle: item.og_title ?? '',
  ogDescription: item.og_description ?? '',
  rssView: Boolean(item.rss_view),
  redirect: item.redirect ?? '',
  body: item.body ?? '',
});

export const articlePreviewFiles = (item: ArticleItem): UploadedFile[] => {
  const preview = toUploadedFile(item.preview);
  return preview ? [preview] : [];
};

export const articleImageFiles = (item: ArticleItem): UploadedFile[] =>
  (item.images ?? []).flatMap((image) => toUploadedFile(image) ?? []);

export const saveArticle = (id: number | undefined, v: ArticleValues) =>
  submitForm<{ success?: number; id?: number; article?: { id: number } }>(
    `/publication/article/${id ? 'update' : 'create'}/`,
    {
      id,
      title: v.title,
      og_title: v.ogTitle,
      slug: v.slug,
      body: v.body,
      category_id: v.categoryId,
      published_time: inputValueToUnix(v.publishedTime),
      status_id: v.statusId,
      og_description: v.ogDescription,
      rss_view: isRssCategory(v.categoryId) ? v.rssView : undefined,
      redirect: v.redirect,
      tag_id: v.tagIds,
    },
  );

const ARTICLE_BASE = '/publication/article';

export const uploadArticlePreview = (articleId: number) => (file: File) =>
  uploadImage(`${ARTICLE_BASE}/upload-preview/`, file, { object_id: articleId });
export const deleteArticlePreview = (id: number) => deleteFile(`${ARTICLE_BASE}/delete-preview/`, id);

export const uploadArticleImage = (articleId: number) => (file: File) =>
  uploadImage(`${ARTICLE_BASE}/upload-image/`, file, { object_id: articleId });
export const deleteArticleImage = (id: number) => deleteFile(`${ARTICLE_BASE}/delete-image/`, id);
